import React, { useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNewOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { Dialog } from "components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { Callout } from "components/common/Callout";
import { TextInput } from "components/common/TextInput";
import { Stack } from "components/common/Stack";
import { SectionRule } from "components/common/SectionRule";
import type { ContactPreviewOutcome } from "lib/api/organizations";
import {
  looksLikeEmail,
  useAddressCheck,
} from "components/main/organizations/useAddressCheck";

const BILLING_DOCS_URL = "https://docs.mycorr.app/billing/";

const MINE = "__mine__";
const OTHER = "__other__";

interface Props {
  open: boolean;
  /// The buyer's own account address, offered as the default.
  accountEmail: string | null;
  /// Another billing account already holds that address; shown disabled rather
  /// than hidden.
  accountEmailTaken: boolean;
  /// Whether a typed address is free for this org; `taken` blocks Continue,
  /// since a charge that lands on another org's customer cannot be undone.
  /// Must be referentially stable: it drives an effect.
  checkAddress: (email: string) => Promise<ContactPreviewOutcome | null>;
  onConfirm: (email: string) => void;
  onCancel: () => void;
}

/// One organization, one address, one Paddle customer.
export const BillingEmailDialog: React.FC<Props> = ({
  open,
  accountEmail,
  accountEmailTaken,
  checkAddress,
  onConfirm,
  onCancel,
}) => {
  const mineAvailable = accountEmail !== null && !accountEmailTaken;
  const [choice, setChoice] = useState<string>(mineAvailable ? MINE : OTHER);
  const [typed, setTyped] = useState("");

  const typedEmail = typed.trim();
  const typedUsable = choice === OTHER && looksLikeEmail(typedEmail);
  const { verdict, checking } = useAddressCheck(
    typedEmail,
    typedUsable,
    checkAddress,
  );

  const typedTaken = verdict === "taken";
  const ready =
    choice === MINE ? mineAvailable : typedUsable && !checking && !typedTaken;

  const rowClass = (selected: boolean, enabled = true) =>
    `flex w-full items-center gap-2 rounded-app border px-3 py-2 text-left transition-colors ${
      enabled ? "hover:bg-bg-hover cursor-pointer" : "cursor-default"
    } ${selected ? "border-accent bg-selected-light" : "border-transparent"}`;

  /// Width held when unselected so the text does not shift.
  const chosenMark = (selected: boolean) =>
    selected ? (
      <CheckIcon className="icon-sm text-accent shrink-0" aria-hidden />
    ) : (
      <span className="icon-sm shrink-0" aria-hidden />
    );

  return (
    <Dialog
      open={open}
      title="Where should this organization be billed?"
      onClose={onCancel}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            onClick={() =>
              onConfirm(choice === MINE ? (accountEmail ?? "") : typedEmail)
            }
            data-testid="confirm-billing-email"
          >
            Continue
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <span className="text-fg-muted-regular">
          Receipts go to the address you pick, and only it can manage payments
          later. Each organization needs an address of its own.
        </span>
        <a
          href={BILLING_DOCS_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="text-fg-muted-regular-small hover:text-fg inline-flex w-fit items-center gap-1"
        >
          How billing works
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </a>
        <div className="flex flex-col">
          <SectionRule label="Choose an email" />
          <div role="radiogroup" aria-label="Choose an email">
            {accountEmail !== null && (
              <button
                type="button"
                role="radio"
                aria-checked={choice === MINE}
                aria-disabled={accountEmailTaken}
                disabled={accountEmailTaken}
                onClick={() => setChoice(MINE)}
                className={rowClass(choice === MINE, !accountEmailTaken)}
                data-testid="billing-email-mine"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={`w-full truncate ${accountEmailTaken ? "text-fg-muted" : "text-fg-medium"}`}
                  >
                    {accountEmail}
                  </span>
                  {accountEmailTaken && (
                    <span className="text-fg-muted-regular-small">
                      Already used for billing elsewhere.
                    </span>
                  )}
                </span>
                {chosenMark(choice === MINE)}
              </button>
            )}
            <button
              type="button"
              role="radio"
              aria-checked={choice === OTHER}
              onClick={() => setChoice(OTHER)}
              className={rowClass(choice === OTHER)}
              data-testid="billing-email-other"
            >
              <span className="text-fg-medium min-w-0 flex-1">
                Another email
              </span>
              {chosenMark(choice === OTHER)}
            </button>
          </div>
          {choice === OTHER && (
            <Stack gap="xs" className="mt-2">
              <TextInput
                value={typed}
                onChange={setTyped}
                type="email"
                ariaLabel="Another email"
                placeholder="billing@yourcompany.com"
                autoFocus
              />
              {typedTaken ? (
                <Callout variant="warning" className="text-fg-regular-small">
                  <span data-testid="billing-email-typed-taken">
                    Already used for billing elsewhere. Pick an address of this
                    organization&apos;s own.
                  </span>
                </Callout>
              ) : null}
            </Stack>
          )}
        </div>
      </Stack>
    </Dialog>
  );
};
